import isMobileWeb from 'is-mobile';
import { useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { MessagingContext } from '@sorare/wallet-shared';
import type { SignUp, Sport as WalletSport } from '@sorare/wallet-shared';

import { Platform, Sport } from '#src/__generated__/globalTypes.ts';
import type { AcceptTermsInfo } from '#src/contexts/connection/index.tsx';
import { useConnectionContext } from '#src/contexts/connection/index.tsx';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useSignUp as useSignUpMutation } from '#src/hooks/auth/useSignUp.ts';
import { useIsMobileApp } from '#src/hooks/useIsMobileApp.ts';
import { useQueryString } from '#src/hooks/useQueryString.ts';
import { useCreateJwtToken } from '#src/hooks/users/useCreateJwtToken.ts';
import { useEvents } from '#src/lib/events/useEvents.ts';
import { LoginWithJWTToken_Sport } from '#src/protos/webview/channel/native_messages.ts';

const readAcceptanceTerms = (
  args: SignUp['request']['args']
): AcceptTermsInfo => {
  return {
    acceptTerms: args.acceptTerms,
    acceptAgeLimit: args.acceptAgeLimit,
    acceptPrivacyPolicy: args.acceptPrivacyPolicy,
    agreedToReceiveOffersFromPartners: args.agreedToReceiveOffersFromPartners,
    acceptGameRules: true,
  };
};

const WALLET_SPORT_TO_SPORT: { [key in WalletSport]: Sport } = {
  FOOTBALL: Sport.FOOTBALL,
  NBA: Sport.NBA,
  BASEBALL: Sport.BASEBALL,
};

const LoginWithJWTTokenSports: Record<WalletSport, LoginWithJWTToken_Sport> = {
  FOOTBALL: LoginWithJWTToken_Sport.FOOTBALL,
  NBA: LoginWithJWTToken_Sport.NBA,
  BASEBALL: LoginWithJWTToken_Sport.BASEBALL,
};

export const useSignUp = () => {
  const { registerHandler } = useContext(MessagingContext)!;
  const signUp = useSignUpMutation();
  const [acceptedTerms, setAcceptedTerms] = useState<AcceptTermsInfo | null>(
    null
  );
  const { refetch } = useCurrentUserContext();
  const { promptTerms, closeConnectionDialog } = useConnectionContext()!;
  const signupPlatform = useQueryString('signupPlatform');
  const track = useEvents();
  const { formatMessage } = useIntl();
  const { isAndroidApp, postMessage } = useIsMobileApp();
  const createJwtToken = useCreateJwtToken();

  useEffect(
    () =>
      registerHandler<SignUp>('signUp', async args => {
        const { mobile, sport, ...rest } = args;

        // Terms and Conditions Modal
        const acceptTerms = acceptedTerms || readAcceptanceTerms(args);

        setAcceptedTerms(acceptTerms);

        let platform: Platform = Platform.WEB;
        if (mobile) {
          if (signupPlatform === 'ios') {
            platform = Platform.IOS;
          } else if (signupPlatform === 'android') {
            platform = Platform.ANDROID;
          }
        } else if (isMobileWeb()) {
          platform = Platform.MOBILE_WEB;
        }

        track('Click Signup By Email', {});
        const { data } = await signUp({
          ...rest,
          platform,
          signupSport: WALLET_SPORT_TO_SPORT[sport],
          certified: 'certified',
          ...acceptTerms,
        });

        if (data!.signUp) {
          const { errors } = data!.signUp;
          if (errors && errors.length !== 0) {
            track('Fail Signup By Email', {});
            return {
              error: errors.reduce<Record<string, string>>((sum, err) => {
                if (err.path?.length !== 2) return sum;
                sum[err.path[1]] = err.message;
                return sum;
              }, {}),
            };
          }
        }

        setAcceptedTerms(null);
        closeConnectionDialog();

        if (data?.signUp?.currentUser) {
          if (isAndroidApp) {
            const { data: jwtData } = await createJwtToken({
              variables: { input: { aud: 'sorare-android' } },
            });
            if (jwtData?.createJwtToken?.jwtToken) {
              postMessage('loginWithJwtToken', {
                email: data.signUp.currentUser.email,
                userId: data.signUp.currentUser.id,
                token: jwtData.createJwtToken.jwtToken.token,
                expiredAt: jwtData.createJwtToken.jwtToken.expiredAt.getTime(),
                sport: LoginWithJWTTokenSports[sport],
              });
            } else {
              postMessage('logout', { isExplicit: true });
            }
          }
        }

        return {};
      }),
    [
      registerHandler,
      signUp,
      promptTerms,
      acceptedTerms,
      closeConnectionDialog,
      refetch,
      signupPlatform,
      track,
      formatMessage,
      isAndroidApp,
      postMessage,
      createJwtToken,
    ]
  );
};
